/*
This file is a helper file that is used to create a policy for the Trusted Types API and a fallback for browsers that do not support it.
The Trusted Types API is a security feature that helps prevent cross-site scripting (XSS) attacks.
 */
export interface TrustedTypeOptions {
  createHTML?: (...args: any[]) => string | undefined;
  createScript?: (...args: any[]) => string | undefined;
  createScriptURL?: (...args: any[]) => string | undefined;
}

/**
 * This map is used to keep track of the policies that have been created.
 * Trusted Types throws an error if a policy with the same name is created more than once.
 */
const registeredTrustedTypesPoliciesMap: { [key: string]: any } = {};

/**
 * This is a fallback for browsers that do not support the Trusted Types API.
 * It is a tinyfill for the API.
 */
const trustedTypesTinyFill = {
  createPolicy: (_: string, options: any) => options,
};
/**
 * Default Trusted Types API coming from the browser.
 */
const defaultTrustedTypesApi =
  typeof self !== "undefined" && self.trustedTypes
    ? self.trustedTypes
    : trustedTypesTinyFill;

/**
 * This function creates a policy for the Trusted Types API.
 * If the policy has already been created, it will skip the creation.
 * @param policyName - The name of the policy.
 * @param policyOptions - The options for the policy.
 * @param trustedTypesApi - The Trusted Types API, this strictly used for testing. Default should be used always in production.
 * @returns The policy.
 */
export const createPolicy = (
  policyName: string,
  policyOptions: TrustedTypeOptions,
  trustedTypesApi = defaultTrustedTypesApi,
  errorLogFunction?: (message: string) => void,
  shouldThrowOnCreationFailure: boolean = false
) => {
  try {
    if (!registeredTrustedTypesPoliciesMap[policyName]) {
      registeredTrustedTypesPoliciesMap[policyName] =
        trustedTypesApi.createPolicy(policyName, {
          createHTML: policyOptions.createHTML,
          createScript: policyOptions.createScript,
          createScriptURL: policyOptions.createScriptURL,
        });
    }
  } catch (e) {
    errorLogFunction?.(`${e}`);
    if (shouldThrowOnCreationFailure) {
      throw e;
    }
  }

  return registeredTrustedTypesPoliciesMap[policyName];
};
